/** Summary footer: aggregation model + the totals row pinned under the grid. */
import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridFooterContainer,
  GridPagination,
  gridFilteredSortedRowEntriesSelector,
  useGridApiContext,
  useGridSelector,
  type GridColDef,
  type GridRowId,
  type GridRowSelectionModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { ROW_DRAG_FIELD, ROW_EXPAND_FIELD } from './styles';

/** Built-in aggregators for the summary footer. */
export type HFSummaryKind = 'sum' | 'avg' | 'count' | 'min' | 'max';

/** Custom aggregators may return a pre-formatted string or a raw value. */
export type HFSummaryAggregator<R extends GridValidRowModel> = (values: unknown[], rows: R[]) => string | number;

export type HFSummaryModel<R extends GridValidRowModel> = Partial<Record<string, HFSummaryKind | HFSummaryAggregator<R>>>;

export type HFSummaryValues = Record<string, string | number>;

/**
 * Fill in default aggregators for numeric columns: every `type: 'number'`
 * column (number / currency / percent / ratio factories) gets `sum` unless the
 * caller overrides it via `fields`. Non-numeric columns stay blank by default.
 */
export function withAutoAggregates<R extends GridValidRowModel>(
  columns: Array<Pick<GridColDef<R>, 'field' | 'type'>>,
  fields: HFSummaryModel<R> = {},
): HFSummaryModel<R> {
  const model: HFSummaryModel<R> = { ...fields };
  for (const col of columns) {
    if (col.type === 'number' && model[col.field] === undefined) model[col.field] = 'sum';
  }
  return model;
}

function numericValues(values: unknown[]): number[] {
  return values.map((value) => (typeof value === 'number' ? value : Number(value))).filter((n) => Number.isFinite(n));
}

/**
 * Compute raw summary values for the footer.
 * Numeric aggregators return numbers so callers can apply the column `valueFormatter`.
 */
export function computeSummary<R extends GridValidRowModel>(rows: R[], model: HFSummaryModel<R>): HFSummaryValues {
  const out: HFSummaryValues = {};

  for (const [field, agg] of Object.entries(model)) {
    if (!agg) continue;
    const values = rows.map((row) => (row as Record<string, unknown>)[field]);

    if (typeof agg === 'function') {
      out[field] = agg(values, rows);
      continue;
    }

    const nums = numericValues(values);
    if (agg === 'count') {
      out[field] = values.filter((v) => v != null && v !== '').length;
      continue;
    }
    if (nums.length === 0) {
      out[field] = '—';
      continue;
    }
    if (agg === 'sum') {
      out[field] = nums.reduce((a, b) => a + b, 0);
      continue;
    }
    if (agg === 'avg') {
      out[field] = nums.reduce((a, b) => a + b, 0) / nums.length;
      continue;
    }
    if (agg === 'min') {
      out[field] = Math.min(...nums);
      continue;
    }
    if (agg === 'max') {
      out[field] = Math.max(...nums);
    }
  }

  return out;
}

export type SummaryFooterProps<R extends GridValidRowModel> = {
  rows: readonly R[];
  fields: HFSummaryModel<R>;
  columns: Array<GridColDef<R>>;
  scope?: 'all' | 'selected';
  label?: string;
  selection?: GridRowSelectionModel;
  /** Drop flattened child rows so totals stay on parents. */
  isChildRow?: (id: GridRowId) => boolean;
};

type FooterColumn = {
  field: string;
  width: number;
  align: 'left' | 'right' | 'center';
};

/** Checkbox / row-drag pseudo columns never carry the label nor a total. */
function isPseudoColumn(field: string): boolean {
  return field === GRID_CHECKBOX_SELECTION_COL_DEF.field || field === ROW_DRAG_FIELD || field === ROW_EXPAND_FIELD;
}

function selectedRows<R extends GridValidRowModel>(rows: readonly R[], selection: GridRowSelectionModel | undefined): R[] {
  if (!selection || selection.type !== 'include' || selection.ids.size === 0) return [...rows];
  return rows.filter((row) => selection.ids.has((row as unknown as { id: string | number }).id));
}

function formatSummaryValue<R extends GridValidRowModel>(field: string, value: string | number, columns: Array<GridColDef<R>>): string {
  if (typeof value === 'string') return value;
  const col = columns.find((c) => c.field === field);
  if (col?.valueFormatter) {
    try {
      return String((col.valueFormatter as (value: unknown, row: R, column: GridColDef<R>, apiRef: unknown) => unknown)(value, {} as R, col, null));
    } catch {
      // fall through
    }
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/**
 * Totals row pinned to the bottom of the grid (footer slot), laid out with the
 * exact visible column widths and kept in sync with horizontal scrolling, so it
 * reads as the grid's last row. The first data column (checkbox / row-drag
 * excluded) always shows the bold label — never a sum. Pagination stays below.
 */
export function SummaryFooter<R extends GridValidRowModel>({
  rows: _rows,
  fields,
  columns,
  scope = 'all',
  label = 'Total',
  selection,
  isChildRow,
}: SummaryFooterProps<R>) {
  const apiRef = useGridApiContext();
  const [footerColumns, setFooterColumns] = useState<FooterColumn[]>([]);
  const [scrollLeft, setScrollLeft] = useState(0);
  const filteredEntries = useGridSelector(apiRef, gridFilteredSortedRowEntriesSelector);
  const filteredRows = useMemo(
    () => filteredEntries.filter((entry) => !isChildRow?.(entry.id)).map((entry) => entry.model as R),
    [filteredEntries, isChildRow],
  );

  useEffect(() => {
    const sync = () => {
      const cols = apiRef.current.getVisibleColumns();
      setFooterColumns(
        cols.map((col) => ({
          field: col.field,
          width: col.computedWidth,
          align: col.align ?? (col.type === 'number' ? 'right' : 'left'),
        })),
      );
    };
    sync();
    const unsubscribes = [
      apiRef.current.subscribeEvent('columnWidthChange', sync),
      apiRef.current.subscribeEvent('columnsChange', sync),
      apiRef.current.subscribeEvent('columnVisibilityModelChange', sync),
      apiRef.current.subscribeEvent('scrollPositionChange', (params) => setScrollLeft(params.left)),
    ];
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [apiRef]);

  const source = useMemo(() => (scope === 'selected' ? selectedRows(filteredRows, selection) : filteredRows), [filteredRows, scope, selection]);

  const values = useMemo(() => computeSummary(source, fields), [source, fields]);

  const firstDataField = footerColumns.find((col) => !isPseudoColumn(col.field))?.field;

  return (
    <GridFooterContainer
      sx={{
        flexDirection: 'column',
        alignItems: 'stretch',
        minHeight: 'auto',
        borderTop: 1,
        borderColor: 'divider',
        p: 0,
      }}
    >
      {/* overflow hidden + translateX keeps the totals visually locked under the columns while scrolling X */}
      <Box sx={{ overflow: 'hidden', bgcolor: 'background.default' }}>
        <Box
          sx={{
            display: 'flex',
            width: 'max-content',
            alignItems: 'center',
            minHeight: 36,
            fontVariantNumeric: 'tabular-nums',
            transform: `translateX(${-scrollLeft}px)`,
          }}
        >
          {footerColumns.map((col) => {
            const isLabelCell = col.field === firstDataField;
            const raw = isLabelCell || isPseudoColumn(col.field) ? undefined : values[col.field];
            const text = isLabelCell ? label : raw === undefined ? '' : formatSummaryValue(col.field, raw, columns);
            return (
              <Typography
                key={col.field}
                component="div"
                variant="caption"
                sx={{
                  width: col.width,
                  flexShrink: 0,
                  // Matches the DataGrid cell horizontal padding so values line up.
                  px: '10px',
                  fontWeight: isLabelCell ? 800 : 700,
                  color: isLabelCell ? 'text.primary' : 'text.secondary',
                  textAlign: isLabelCell ? 'left' : col.align,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {text}
              </Typography>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: 1, borderColor: 'divider', minHeight: 44 }}>
        <GridPagination />
      </Box>
    </GridFooterContainer>
  );
}
